/**
 * ONE bounded-retry rule for an AMBIENT gateway 5xx, shared by every HTTP transport in the app.
 *
 * INFRA-8. The production ALB emits a small, constant trickle of 5xx that never reaches the
 * application at all — CloudWatch over 8h: HTTPCode_ELB_502_Count=6 with Target_5XX=0 and
 * TargetConnectionError=0 across 2272 requests. Every one of them landed in a panel as
 * "Couldn't load"; all of them returned 200 on the very next attempt. One 85-route owner sweep hit
 * a 502 on four unrelated endpoints in a single pass.
 *
 * This module exists because the first fix installed the rule on ONE of the app's transports.
 * There are five, and the three that carry the highest request volume were the unprotected ones:
 *
 *   src/lib/api.js        staff/admin        (fixed first)
 *   src/lib/parentApi.js  every guardian read of fees / attendance / marks   <- 47 call sites
 *   src/lib/studentApi.js every student portal read
 *   pages/Help.js         `/help` — a PRERENDERED marketing route whose three public GETs use a
 *                         private axios instance, so the build-time proxy retry in
 *                         scripts/prerender.mjs protected the SNAPSHOT and nothing protected the
 *                         reader's browser
 *   pages/Contact.js      same private instance
 *   pages/PublicSite.js   raw fetch() on the tenant sites   -> fetchWithRetry below
 *
 * A per-file copy of this rule is how the two halves of a safety property drift apart (the G4-1
 * provisioning-grant precedent). Import it; never re-implement it.
 *
 * WHAT MUST NEVER BE RETRIED, AND WHY
 *
 * 1. A NON-IDEMPOTENT method. A replayed POST is a second fee collection, a second refund, a second
 *    payout. Money paths are idempotent server-side by KEY, never by luck — do not lean on that
 *    here. GET/HEAD/OPTIONS only.
 * 2. A response the APPLICATION produced. This is the load-bearing exclusion. `core/observability`
 *    stamps `X-Request-ID` on every response FastAPI emits (outermost pure-ASGI middleware), and an
 *    ALB-generated error carries none — so the header is a direct, positive signal that the request
 *    reached and was handled by the backend. Replaying that is not a free retry: `GET
 *    /api/student-ai/{sid}/snapshot` and `/{sid}/risk-explain` are credit-METERED LLM reads, so a
 *    failure raised after `record_usage` would charge the school's AI credits a second time.
 *    (The SPA is served same-origin with the API behind one CloudFront distribution in prod — `/api/*`
 *    is a behavior on the same host — and REACT_APP_BACKEND_URL is "" for local dev (relative `/api`,
 *    proxied). It is NOT "" everywhere: the e2e CI job deliberately runs the SPA on :3000 against the
 *    API on :8001 (REACT_APP_BACKEND_URL=http://localhost:8001), a genuinely cross-origin request —
 *    proven by `hr-payroll-ai-model-drift.spec.js`/`hr-payroll-ai064.spec.js` failing only in CI, never
 *    locally, until reproduced by running the frontend with that same env var. A cross-origin response
 *    exposes to JS only the CORS-safelisted header set unless `Access-Control-Expose-Headers` names
 *    the rest, so this header's READABILITY is not a topology accident to assume — it is now a
 *    contract: `backend/app/server.py`'s CORSMiddleware sets `expose_headers=["X-Request-ID"]`, and
 *    `e2e/fixtures/appResponse.js`'s mock carries the matching header. Where it is somehow still not
 *    readable the rule falls back to the status set below, which is already narrow.)
 * 3. Status 500. Same reason as (2) and it does not depend on a header being visible: 500 is the
 *    application's own failure status, so it proves the request was processed. Only the pure
 *    gateway statuses are retried.
 * 4. 501 Not Implemented — a deliberate contract in this codebase (the mandate resolver refusing to
 *    fall back to a simulated provider). It will never become a 200.
 * 5. The EDGE-01 synthetic "html masquerading as 200" error (status 0) and an aborted/cancelled
 *    request.
 *
 * RELATIONSHIP TO scripts/prerender.mjs (stated honestly, because an earlier comment here claimed a
 * parity that does not exist): `proxyApi` retries GET/HEAD on ANY status >= 500, twice, 750ms
 * linear. It is deliberately BROADER — it is a build-time crawler with no user, no money path and
 * no metered read, and its failure mode is baking fallback copy into a shipped snapshot. The
 * browser rule is deliberately NARROWER for the reasons above. The two are siblings in shape, not
 * in set, and nothing enforces that they match.
 *
 * LATENCY: 2 retries at 400ms then 800ms => at most ~1.2s of added delay on this layer before the
 * error surfaces. A read that also goes through `lib/queryClient` inherits its `retry: 1`, which
 * retries a 5xx once more at the QUERY level — so a persistently-5xx useQuery read costs up to
 * 6 network calls and ~2.4s before it renders its error. That is the honest ceiling.
 */
import axios from "axios";

/** Gateway-only. 500 is excluded on purpose — see (3) above. */
export const RETRYABLE_GATEWAY_5XX = new Set([502, 503, 504]);
export const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
export const HTTP_5XX_RETRIES = 2;

let retryDelayMs = 400; // 400ms, then 800ms
export const _setRetryDelayMs = (ms) => { retryDelayMs = ms; }; // test hook
export const _retryDelayMs = () => retryDelayMs;

// HTTP header names are case-INSENSITIVE, and this lookup is a safety boundary: a miss here is a
// silent fail-OPEN (an application response gets classified as a gateway error and replayed, which
// is what re-bills a metered LLM read). axios v1 hands us an AxiosHeaders with `.get()` and raw
// `fetch` a `Headers`, so both real transports take the first branch — but the plain-object
// fallback previously lowercased only the NAME, which is already lowercase at every call site, so
// a bag keyed `X-Request-ID` read as absent. Scan the keys when the direct hits miss.
const header = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") return headers.get(name);
  const lower = name.toLowerCase();
  const direct = headers[name] ?? headers[lower];
  if (direct !== undefined) return direct;
  const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === lower);
  return key === undefined ? undefined : headers[key];
};

/** True when the backend itself answered — i.e. the request was processed. Never replay it. */
export function isApplicationResponse(response) {
  return Boolean(header(response && response.headers, "x-request-id"));
}

export function shouldRetryIdempotent(error) {
  const config = error && error.config;
  if (!config || error.isEdgeMaskedError) return false;
  if (axios.isCancel && axios.isCancel(error)) return false;
  if (config.signal && config.signal.aborted) return false;
  if ((config.__vp5xxAttempts || 0) >= HTTP_5XX_RETRIES) return false;
  if (!IDEMPOTENT_METHODS.has(String(config.method || "get").toLowerCase())) return false;
  const response = error.response;
  if (!response || !RETRYABLE_GATEWAY_5XX.has(response.status)) return false;
  return !isApplicationResponse(response);
}

/**
 * Install the rule on an axios instance.
 *
 * Register this BEFORE any other response interceptor on the instance: axios runs the response
 * chain in registration order, so this keeps a transient 502 from waking a later handler (the
 * parent/student 401->refresh branch, `handleRuntimeAccessError`) for a request that is about to
 * succeed. Those handlers still run when the retries are exhausted.
 */
export function installIdempotentRetry(instance) {
  instance.interceptors.response.use(undefined, async (error) => {
    if (!shouldRetryIdempotent(error)) return Promise.reject(error);
    const config = error.config;
    config.__vp5xxAttempts = (config.__vp5xxAttempts || 0) + 1;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs * config.__vp5xxAttempts));
    // Re-enter through the instance so its REQUEST interceptors re-apply (X-Tenant-Slug, the
    // conditional-GET revalidation). The attempt counter rides on the config, so a retry that
    // fails again lands back here and stops at the cap.
    return instance.request(config);
  });
  return instance;
}

/**
 * The same rule for a raw `fetch` GET (pages/PublicSite.js). Non-idempotent methods and any
 * response the application produced are returned untouched, exactly as above.
 */
export async function fetchWithRetry(input, init) {
  const method = String((init && init.method) || "GET").toLowerCase();
  let response = await fetch(input, init);
  if (!IDEMPOTENT_METHODS.has(method)) return response;
  for (let attempt = 1; attempt <= HTTP_5XX_RETRIES; attempt += 1) {
    if (!RETRYABLE_GATEWAY_5XX.has(response.status)) break;
    if (isApplicationResponse(response)) break;
    if (init && init.signal && init.signal.aborted) break;
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs * attempt));
    response = await fetch(input, init);
  }
  return response;
}
